import InstrumentInstanceModel from "@/models/lab/InstrumentInstanceModel";
import lodash from "lodash";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

export const random = (min, max) => {
  return Math.floor(Math.random() * (max - min + 1) + min);
};

export const formatAnalysisName = ({ analyte, measurement_method, name }) => `
  ${analyte.sample_type ? analyte.sample_type.name : "General"}
  ${analyte.name}: ${measurement_method || name}
`;

export const formatAnalyteName = ({ sample_type, name }) =>
  `${sample_type ? sample_type.name : "General"} ${name}`;

export const formatAnalyses = (task) => {
  const analyses = task.getAnalyses();

  return analyses.models.length
    ? analyses.models
      .map((analysis) => formatAnalysisName(analysis))
      .sort()
      .join(", ")
    : null;
};

export const generatePid = ({ entity, prefix }) => {
  return `${prefix}-${String(entity.pid).padStart(6, "0")}`;
}

export const shuffleArray = (array) => {
  for (let i = array.length - 1; i > 0; i--) {
    let j = Math.floor(Math.random() * (i + 1));
    let temp = array[i];
    array[i] = array[j];
    array[j] = temp;
  }

  return array;
};

export const getEntityData = ({ entity, levels, field }) => {
  const data_deleted_message = "deleted";
  const no_data_message = "unavailable";
  const lvl = levels[0];

  if (levels.length === 1) {
    return entity[lvl]
      ? typeof field === "string"
        ? entity[lvl][field]
        : field.map((sub_field) => entity[lvl][sub_field]).join(": ")
      : entity[`${lvl}_id`]
        ? data_deleted_message
        : no_data_message;
  } else {
    return getEntityData({
      entity: entity[lvl] || {},
      levels: levels.filter((level, index) => index !== 0),
      field,
    });
  }
};

export const debounce = (fn, ms) => {
  let timeout;

  return function () {
    const fnCall = () => {
      fn.apply(this, arguments);

      clearTimeout(timeout);

      timeout = setTimeout(fnCall, ms);
    };
  };
};

export const cloneAttributes = (
  original,
  exclude_obj,
  exclude_arr,
  exclude_keys
) => {
  exclude_keys = [...exclude_keys, "created_at", "updated_at", "deleted_at"];

  const cloned = {};

  for (const key of Object.keys(original)) {
    if (
      !exclude_keys.includes(key) &&
      (!exclude_obj || (exclude_obj && typeof original[key] !== "object")) &&
      (!exclude_arr || (exclude_arr && !lodash.isArray(original[key])))
    ) {
      cloned[key] = original[key];
    }
  }

  return cloned;
};

export const getInitialInstanceData = (process) => {

  let result;
  if (process.schedulable_process.schedulable_tasks.length) {
    result = process.process_schedule_instrument_instances.find(
      (item) => {
        for (const sched_task of process.schedulable_process.schedulable_tasks) {
          if (item.process_task_id === sched_task.id) return true;
        }
        return false;
      }
    );
  }

  // make sure an object is returned
  return (typeof result === 'object') ? result : {};
};

export const getTaskInstances = (process, task) => {
  return process.process_schedule_instrument_instances.reduce((acc, cur) => {
    let instrument_check;
    if (task.instrument_instances) {
      instrument_check = task.instrument_instances.find(
        (item) =>
          item.id === cur.instrument_instance_id &&
          task.id === cur.process_task_id
      );
    }
    return instrument_check
      ? [...acc, new InstrumentInstanceModel(instrument_check)]
      : acc;
  }, []);
};

export const getTabData = (process, children, index) => {
  return process.process_schedule_instrument_instances.find(
    (item) =>
      item.process_task_id ===
      process.schedulable_process.schedulable_tasks[children[index].currentTab]
        .id
  );
};

/**
 * Take the App\Models\* and return the * 
 * @param {string} str the model formmated string
 * @returns the model without the app\models infront
 */
export const formatModelPath = (str) => {
  const strArr = str.split("\\");

  return strArr[strArr.length-1];
}

/**
 * Given PrepTask remove the Task
 * @param {string} str 
 */
export const stripTaskFromName = (str) => {
  return str.replace(/([A-Z])/g, ' $1')
      .split(" ")
      .filter(s => s)[0];
}

/**
 * Group the incoming array by a given key
 * @param {array} arr incoming array to be reduced to an object
 * @param {string} key the key that the array will be grouped on
 * @returns an object with the key being the value of the key variable and the value an array of values associated to the key
 */
export const groupBy = (arr, key) => {
  // reduce the incoming array into an object
  return arr.reduce((rowValue, x) => {
    // set the header to be the incoming key
    (rowValue[x[key]] = rowValue[x[key]] || []).push(x);
    return rowValue;
  }, {});
}

export const trimInput = (str) => {
  const dblSpace = str.match(/\s\s/);
  let trimedStr = str;
  str[0] === " " && (trimedStr = str.trim());
  dblSpace &&
    dblSpace.index &&
    (trimedStr = str.slice(0, dblSpace.index) + str.slice(dblSpace.index + 1));

  return trimedStr;
};

export const generateUrl = ({ connections, filters, limit, page, sort }) => {
  const connection_list = connections
    ? connections
      .map((connection) => {
        return `&connection[]=${connection}`;
      })
      .join("")
    : "";
  const filter_list = filters
    ? filters
      .reduce((acc, { field, operator, logic, value }) => {
        return value.length
          ? [
            ...acc,
            `&filter[]=${field}:${operator || "eq"}:${value.join("|")}:${logic || "where"
            }`,
          ]
          : acc;
      }, [])
      .join("")
    : "";
  const sort_list = sort
    ? sort.map(({ field, value }) => `&sort[]=${field}:${value}`).join("")
    : "";

  return `?limit=${limit || 10}&page=${page || 1
    }${connection_list}${filter_list}${sort_list}`;
};

export const getReportType = (sample_type) => {
  switch (sample_type) {
    case "Septic Soil":
      return "septic";
    case "Feed Mixer Efficiency":
      return "mixer_efficiency";
    case "Irrigation Suitability":
      return "sar";
    case "Potato":
      return "western_tractor";
    case "Soil":
    case "Tissue":
    case "Feed":
    case "Water":
      return sample_type.toLowerCase();
    default:
      return "general";
  }
};

export const downloadHtmlCanvas = (ref, fileName) => {
  html2canvas(ref).then(canvas => {
    const pdf = new jsPDF(ref.offsetHeight > ref.offsetWidth ? "p" : "l", "pt", [
      ref.offsetWidth,
      ref.offsetHeight,
    ]);

    pdf.addImage(canvas.toDataURL(), 'PNG', 0, 0, ref.offsetWidth, ref.offsetHeight)
    pdf.save(fileName)
  });
}
